import { useEffect, useState, type FormEvent } from 'react';
import { Button } from '@/components/ui/Button';
import { Field } from '@/components/ui/Field';
import { Input } from '@/components/ui/Input';
import { KeyValueFields } from '@/components/ui/KeyValueFields';
import { Modal } from '@/components/ui/Modal';
import { Switch } from '@/components/ui/Switch';
import { Textarea } from '@/components/ui/Textarea';
import { FormErrorAlert } from '@/features/auth/FormErrorAlert';
import { useActiveTenantAccess } from '@/hooks/use-active-tenant';
import { getErrorMessage } from '@/lib/api-error';
import {
  EMPTY_FORM_ERRORS,
  fieldError,
  formErrorsFromApiError,
  type FormErrors,
} from '@/lib/form-errors';
import {
  emptyKeyValueRow,
  keyValueRowsFromRecord,
  recordFromKeyValueRows,
  type KeyValueRow,
} from '@/lib/key-value';
import type { PaymentMethod } from '@/types/settings';
import { validatePaymentMethod } from './settings-validation';
import { useCreatePaymentMethod, useUpdatePaymentMethod } from './use-payment-methods';

/**
 * Alta y edición de un método de pago (`POST` / `PATCH .../payment-methods`, solo ADMIN).
 *
 * `datos` es un JSON libre en el backend (número de cuenta, titular…), así que se edita como
 * pares clave/valor (`KeyValueFields`) y se convierte con `recordFromKeyValueRows`.
 *
 * Detalle del contrato que se respeta a propósito:
 *  - al CREAR, `datos` y `condiciones` vacíos se omiten del body;
 *  - al EDITAR, `condiciones` vacía viaja como `null` (el backend acepta `null` para vaciar;
 *    `undefined` solo significa "no toques este campo").
 */

export interface PaymentMethodDialogProps {
  open: boolean;
  onClose: () => void;
  /** `null` = crear; con método = editar ese método. */
  paymentMethod: PaymentMethod | null;
}

const PAYMENT_FIELDS = ['nombre', 'tipo', 'condiciones', 'datos'] as const;

export function PaymentMethodDialog({ open, onClose, paymentMethod }: PaymentMethodDialogProps) {
  const { tenantId } = useActiveTenantAccess();
  const createPaymentMethod = useCreatePaymentMethod(tenantId);
  const updatePaymentMethod = useUpdatePaymentMethod(tenantId);

  const [nombre, setNombre] = useState('');
  const [tipo, setTipo] = useState('');
  const [condiciones, setCondiciones] = useState('');
  const [activo, setActivo] = useState(true);
  const [datos, setDatos] = useState<KeyValueRow[]>([emptyKeyValueRow()]);
  const [errors, setErrors] = useState<FormErrors>(EMPTY_FORM_ERRORS);
  const [submitError, setSubmitError] = useState<string | null>(null);

  /** Cada apertura arranca limpia (o con los datos del método que se va a editar). */
  useEffect(() => {
    if (!open) return;

    setNombre(paymentMethod?.nombre ?? '');
    setTipo(paymentMethod?.tipo ?? '');
    setCondiciones(paymentMethod?.condiciones ?? '');
    setActivo(paymentMethod?.activo ?? true);
    setDatos(keyValueRowsFromRecord(paymentMethod?.datos ?? null));
    setErrors(EMPTY_FORM_ERRORS);
    setSubmitError(null);
  }, [open, paymentMethod]);

  const saving = createPaymentMethod.isPending || updatePaymentMethod.isPending;
  const isEditing = paymentMethod !== null;

  async function handleSubmit(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();

    const nextErrors = validatePaymentMethod({ nombre, tipo, condiciones, datos });
    if (Object.keys(nextErrors.fieldErrors).length > 0) {
      setErrors(nextErrors);
      setSubmitError(null);
      return;
    }

    const record = recordFromKeyValueRows(datos);
    const cleanName = nombre.trim();
    const cleanType = tipo.trim();
    const cleanConditions = condiciones.trim();

    setErrors(EMPTY_FORM_ERRORS);
    setSubmitError(null);

    try {
      if (isEditing) {
        await updatePaymentMethod.mutateAsync({
          paymentMethodId: paymentMethod.id,
          payload: {
            nombre: cleanName,
            tipo: cleanType,
            activo,
            datos: record,
            condiciones: cleanConditions.length > 0 ? cleanConditions : null,
          },
        });
      } else {
        await createPaymentMethod.mutateAsync({
          nombre: cleanName,
          tipo: cleanType,
          activo,
          ...(record ? { datos: record } : {}),
          ...(cleanConditions.length > 0 ? { condiciones: cleanConditions } : {}),
        });
      }

      onClose();
    } catch (error) {
      setErrors(formErrorsFromApiError(error, PAYMENT_FIELDS));
      setSubmitError(getErrorMessage(error));
    }
  }

  return (
    <Modal
      open={open}
      onClose={onClose}
      size="lg"
      title={isEditing ? 'Editar método de pago' : 'Nuevo método de pago'}
      description="El bot ofrece solo los métodos activos y responde con estos datos y condiciones, sin inventar nada."
      footer={
        <>
          <Button variant="secondary" onClick={onClose} disabled={saving}>
            Cancelar
          </Button>
          {/* `form` enlaza este botón del pie con el formulario del cuerpo del modal. */}
          <Button type="submit" form="payment-method-form" loading={saving}>
            {isEditing ? 'Guardar cambios' : 'Crear método'}
          </Button>
        </>
      }
    >
      <form id="payment-method-form" onSubmit={handleSubmit} noValidate className="space-y-5">
        <FormErrorAlert message={submitError} errors={errors} />

        <div className="grid gap-4 sm:grid-cols-2">
          <Field
            label="Nombre"
            htmlFor="paymentNombre"
            required
            hint="Tal como se lo muestras al cliente."
            error={fieldError(errors, 'nombre')}
          >
            <Input
              id="paymentNombre"
              name="nombre"
              placeholder="Ej. Nequi"
              autoFocus
              value={nombre}
              invalid={Boolean(fieldError(errors, 'nombre'))}
              onChange={(event) => setNombre(event.target.value)}
            />
          </Field>

          <Field
            label="Tipo"
            htmlFor="paymentTipo"
            required
            hint="Transferencia, contraentrega, pasarela…"
            error={fieldError(errors, 'tipo')}
          >
            <Input
              id="paymentTipo"
              name="tipo"
              placeholder="Ej. transferencia"
              value={tipo}
              invalid={Boolean(fieldError(errors, 'tipo'))}
              onChange={(event) => setTipo(event.target.value)}
            />
          </Field>
        </div>

        <KeyValueFields
          rows={datos}
          onChange={setDatos}
          keyLabel="Dato"
          valueLabel="Valor"
          addLabel="Agregar dato"
          keyPlaceholder="ej. cuenta"
          valuePlaceholder="ej. 3001234567"
        />

        {fieldError(errors, 'datos') ? (
          <p className="text-micro text-danger-strong">{fieldError(errors, 'datos')}</p>
        ) : (
          <p className="text-micro text-ink-faint">
            Lo que el cliente necesita para pagar: número de cuenta o celular, titular, banco…
          </p>
        )}

        <Field
          label="Condiciones"
          htmlFor="paymentCondiciones"
          hint="Opcional. Ej. «enviar el comprobante antes de despachar» o «solo disponible en Cali»."
          error={fieldError(errors, 'condiciones')}
        >
          <Textarea
            id="paymentCondiciones"
            name="condiciones"
            rows={3}
            value={condiciones}
            invalid={Boolean(fieldError(errors, 'condiciones'))}
            onChange={(event) => setCondiciones(event.target.value)}
          />
        </Field>

        <div className="flex items-center justify-between gap-4 rounded-panel border border-line bg-surface-muted/50 px-4 py-3">
          <div className="min-w-0">
            <p className="text-body font-medium text-ink">Método activo</p>
            <p className="text-caption text-ink-muted">
              Si lo desactivas deja de ofrecerse, pero no se borra (útil si es temporal).
            </p>
          </div>

          <Switch checked={activo} onChange={setActivo} label="Método activo" disabled={saving} />
        </div>
      </form>
    </Modal>
  );
}

